"use client"
import { memo, useMemo } from 'react'
import NumberFlow from '@number-flow/react'
import Tooltip from '@/ui/Tooltip'
import { AnimateYear } from './AnimateYear'
import { UnemploymentIndicators } from './UnemploymentIndicators'
import { parseEnergy } from './economic-data/calculations'
import { type PlanAPhaseData, getDataForDate } from "@/data/plan-a/planATypes"
// UBI Timeline based on plan-a-v4.md:
// - 2032: US passes UBI, funded by 5% compute/robot tax
// - 2035: $6,000/year nominal per US adult, but 100-1000x deflation means huge purchasing power
// - 2037: Global UBI starts at $1,000/year for ~5B people (excl. China)
// - Rent ~$200/month = 40% of UBI, so monthly UBI = $500 = $6,000/year
// - Life expectancy rises due to AI-accelerated medicine
type WelfareDataPoint = {
  year: number
  ubiUS: number              // Nominal USD/year
  ubiChina: number           // Nominal USD/year (their own program)
  ubiWorld: number           // Nominal USD/year (US-funded global program starts 2037)
  purchasingMultiplier: number  // How much more each dollar buys due to deflation
  lifeExpectancy: number     // Global average in years
}
// UBI starts 2032 (US), 2033 (China follows), 2037 (global)
// Purchasing power multiplier represents how much more goods cost has deflated
const welfareTimeline: WelfareDataPoint[] = [
  { year: 2030, ubiUS: 0, ubiChina: 0, ubiWorld: 0, purchasingMultiplier: 1, lifeExpectancy: 75 },
  { year: 2031, ubiUS: 0, ubiChina: 0, ubiWorld: 0, purchasingMultiplier: 1.5, lifeExpectancy: 77 },
  { year: 2032, ubiUS: 2000, ubiChina: 0, ubiWorld: 0, purchasingMultiplier: 3, lifeExpectancy: 79 },
  { year: 2033, ubiUS: 3500, ubiChina: 1500, ubiWorld: 0, purchasingMultiplier: 8, lifeExpectancy: 82 },
  { year: 2034, ubiUS: 5000, ubiChina: 2500, ubiWorld: 0, purchasingMultiplier: 25, lifeExpectancy: 88 },
  { year: 2035, ubiUS: 6000, ubiChina: 3500, ubiWorld: 0, purchasingMultiplier: 100, lifeExpectancy: 95 },
  { year: 2036, ubiUS: 8000, ubiChina: 5000, ubiWorld: 0, purchasingMultiplier: 300, lifeExpectancy: 105 },
  { year: 2037, ubiUS: 12000, ubiChina: 8000, ubiWorld: 1000, purchasingMultiplier: 500, lifeExpectancy: 115 },
  { year: 2038, ubiUS: 18000, ubiChina: 12000, ubiWorld: 2000, purchasingMultiplier: 800, lifeExpectancy: Infinity },
]
export const getWelfareForDate = (date: Date): WelfareDataPoint => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const currentYearData = welfareTimeline.find(d => d.year === year)
  const nextYearData = welfareTimeline.find(d => d.year === year + 1)
  if (!currentYearData) {
    if (year < 2030) return welfareTimeline[0]
    return welfareTimeline[welfareTimeline.length - 1]
  }
  if (!nextYearData) return currentYearData
  const t = month / 12
  return {
    year,
    ubiUS: Math.round(currentYearData.ubiUS + (nextYearData.ubiUS - currentYearData.ubiUS) * t),
    ubiChina: Math.round(currentYearData.ubiChina + (nextYearData.ubiChina - currentYearData.ubiChina) * t),
    ubiWorld: Math.round(currentYearData.ubiWorld + (nextYearData.ubiWorld - currentYearData.ubiWorld) * t),
    purchasingMultiplier: Math.round((currentYearData.purchasingMultiplier + (nextYearData.purchasingMultiplier - currentYearData.purchasingMultiplier) * t) * 10) / 10,
    lifeExpectancy: nextYearData.lifeExpectancy === Infinity ? Math.round((currentYearData.lifeExpectancy + (130 - currentYearData.lifeExpectancy) * t) * 10) / 10 : Math.round((currentYearData.lifeExpectancy + (nextYearData.lifeExpectancy - currentYearData.lifeExpectancy) * t) * 10) / 10,
  }
}
type WelfareRowProps = {
  label: string
  value: number
  max: number
  format: 'ubi' | 'years' | 'multiplier' | 'percent' | 'compute' | 'energy' | 'robots' | 'count' | 'months' | 'researchers'
  tooltip?: string
  textColor: string
}
const WelfareValueDisplay = ({
  value,
  format,
}: {
  value: number
  format: WelfareRowProps['format']
}) => (
  <>
    {format === 'ubi' ? (
      <><NumberFlow value={value} format={{ style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }} />/yr</>
    ) : format === 'compute' ? (
      <><NumberFlow value={value} format={{ notation: 'compact', maximumFractionDigits: 0 }} /> PFLOP/s</>
    ) : format === 'energy' ? (
      <><NumberFlow value={value} format={{ notation: 'compact', maximumFractionDigits: 1 }} /> TW</>
    ) : format === 'robots' ? (
      <><NumberFlow value={value} format={{ notation: 'compact', maximumFractionDigits: 0 }} /> robots</>
    ) : format === 'count' ? (
      <><NumberFlow value={value} format={{ maximumFractionDigits: 0 }} /></>
    ) : format === 'months' ? (
      <><NumberFlow value={value} format={{ maximumFractionDigits: 0 }} /> months</>
    ) : format === 'researchers' ? (
      <><NumberFlow value={value} format={{ notation: 'compact', maximumSignificantDigits: 2 }} /> researchers</>
    ) : format === 'multiplier' ? (
      <><NumberFlow value={value} format={{ maximumFractionDigits: 0 }} />x</>
    ) : format === 'percent' ? (
      <><NumberFlow value={value} format={{ maximumFractionDigits: 1 }} />%</>
    ) : value === Infinity ? (
      <>??? yrs</>
    ) : (
      <><NumberFlow value={value} format={{ maximumFractionDigits: 0 }} /> yrs</>
    )}
  </>
)

const BAR_FILLED_COLOR = 'var(--accent)'
const BAR_BACKGROUND_COLOR = 'transparent'
const TEXT_FILLED_COLOR = 'white'

export const WelfareRow = ({
  label,
  value,
  max,
  format,
  tooltip,
  textColor,
}: WelfareRowProps) => {
  const fillPct = value === Infinity ? 100 : (max > 0 ? Math.min((value / max) * 100, 100) : 0)
  const content = (
    <div className="relative h-6 overflow-hidden mb-0.5" style={{ background: BAR_BACKGROUND_COLOR, borderRadius: 3 }}>
      {/* Filled bar */}
      <div
        className="absolute inset-0 h-full transition-all duration-500 ease-in-out"
        style={{ width: `${fillPct}%`, background: BAR_FILLED_COLOR }}
      />
      {/* Text overlay - base layer (unfilled color) */}
      <div
        className="absolute inset-0 flex items-center justify-between pl-2 text-[10px]"
        style={{ color: textColor }}
      >
        <span className="">{label}</span>
        <span className="font-bold tabular-nums"><WelfareValueDisplay value={value} format={format} /></span>
      </div>
      {/* Text overlay - filled color layer with mask */}
      <div
        className="absolute inset-0 flex items-center justify-between px-2 text-[10px] pointer-events-none"
        style={{
          color: TEXT_FILLED_COLOR,
          maskImage: `linear-gradient(to right, black ${fillPct}%, transparent ${fillPct}%)`,
          WebkitMaskImage: `linear-gradient(to right, black ${fillPct}%, transparent ${fillPct}%)`,
        }}
      >
        <span>{label}</span>
        <span className="font-bold tabular-nums"><WelfareValueDisplay value={value} format={format} /></span>
      </div>
    </div>
  )
  if (tooltip) {
    return <Tooltip content={<div className="p-2 text-[10px] max-w-[200px]">{tooltip}</div>}><div>{content}</div></Tooltip>
  }
  return content
}
export const WelfareRowTable = ({
  label,
  value,
  max: _max,
  format,
  tooltip,
  textColor,
}: WelfareRowProps) => {
  void _max
  const content = (
    <div className="flex items-center justify-between text-[10px]" style={{ color: textColor }}>
      <span>{label}</span>
      <span className="font-bold tabular-nums"><WelfareValueDisplay value={value} format={format} /></span>
    </div>
  )
  if (tooltip) {
    return <Tooltip content={<div className="p-2 text-[10px]">{tooltip}</div>}><div>{content}</div></Tooltip>
  }
  return content
}

export const WelfareIndicators = memo(({
  currentDate,
  planAData,
  textColor,
}: {
  currentDate: Date
  planAData?: PlanAPhaseData[]
  textColor: string
}) => {
  const currentPhase = useMemo(
    () => planAData ? getDataForDate(planAData, currentDate) : null,
    [planAData, currentDate]
  )
  const computeValues = currentPhase?.economic
  const data = getWelfareForDate(currentDate)
  const totalCompute = (computeValues?.computeUS || 0) + (computeValues?.computeChina || 0) + (computeValues?.computeRest || 0)
  const totalEnergyUSChina = parseEnergy(computeValues?.energyUS || '') + parseEnergy(computeValues?.energyChina || '')
  const totalRobots = (computeValues?.robotsUS || 0) + (computeValues?.robotsChina || 0) + (computeValues?.robotsRest || 0)
  const year = currentDate.getFullYear()
  return (
    <div className="mt-1.5">
      <div className="flex gap-0.5 flex-col transition-opacity duration-200 ease-in-out" style={{ opacity: year >= 2027 ? 1 : 1 }}>
        {/* <WelfareRowTable
          label="Compute"
          value={totalCompute}
          max={150}
          format="compute"
          tooltip="How much compute is available in datacenters across the world. Compute enables larger training runs to train more powerful AI, and to run instances of AI models."
          textColor={textColor}
        />
        <WelfareRowTable
          label="Energy"
          value={totalEnergyUSChina}
          max={400}
          format="energy"
          tooltip="Combined energy usage for the US and China."
          textColor={textColor}
        />
        <WelfareRowTable
          label="Robots"
          value={totalRobots}
          max={30000000000}
          format="robots"
          tooltip="How many robots are available to do automated physical labor."
          textColor={textColor}
        /> */}
        <UnemploymentIndicators
          currentDate={currentDate}
          textColor={textColor}
        />
        <div className="">
          <WelfareRowTable
            label="Life Expectancy"
            value={data.lifeExpectancy}
            max={150}
            format="years"
            tooltip="Global average life expectancy. AI accelerates drug discovery, personalized medicine, and eliminates most diseases."
            textColor={textColor}
          />
        </div>
        <AnimateYear targetYear={2031}>
          <WelfareRowTable
            label="Deflation"
            value={data.purchasingMultiplier}
            max={1000}
            format="multiplier"
            tooltip="How much more each dollar buys compared to 2025. AI collapses cost of cognitive labor (100-1000x) and physical goods (100x)."
            textColor={textColor}
          />
        </AnimateYear>
        <AnimateYear targetYear={2032}>
          <WelfareRowTable
            label="AI Dividend (US)"
            value={data.ubiUS * data.purchasingMultiplier}
            max={24000}
            format="ubi"
            tooltip={`Nominal $${data.ubiUS.toLocaleString()}/year. With ${data.purchasingMultiplier}x deflation, buys what $${Math.round(data.ubiUS * data.purchasingMultiplier).toLocaleString()} would in 2025. Funded by 5% compute/robot tax.`}
            textColor={textColor}
          />
        </AnimateYear>
        <AnimateYear targetYear={2033}>
          <WelfareRowTable
            label="AI Dividend (China)"
            value={data.ubiChina * data.purchasingMultiplier}
            max={24000}
            format="ubi"
            tooltip={`China's parallel dividend program. Nominal $${data.ubiChina.toLocaleString()}/year, effective purchasing power ~$${Math.round(data.ubiChina * data.purchasingMultiplier).toLocaleString()}.`}
            textColor={textColor}
          />
        </AnimateYear>
        <AnimateYear targetYear={2037}>
          <WelfareRowTable
            label="Citizen’s Dividend (World)"
            value={data.ubiWorld * data.purchasingMultiplier}
            max={24000}
            format="ubi"
            tooltip={`US-funded global Citizen’s Dividend for ~5 billion people (excl. China). Funded by additional 13% compute tax. Nominal $${data.ubiWorld.toLocaleString()}/year.`}
            textColor={textColor}
          />
        </AnimateYear>
      </div>
    </div>
  )
})
WelfareIndicators.displayName = "WelfareIndicators"
